/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TelephonyDomainsFormView.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Telephony domains creation and editing form.
────────────────────────────────────────────────────────────────*/

import { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Copy, Globe, Plus, Network } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { useAuthStore } from '../../store/auth';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { FormBox } from '../../components/ui/FormBox';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { navigateToTelephonyDomainDuplicate } from './telephonyDomainsActions';
import { useDomainData } from './hooks/useDomainData';
import { useDomainForm } from './hooks/useDomainForm';
import { useDomainSubmit } from './hooks/useDomainSubmit';
import { useQuickList } from './hooks/useQuickList';
import { useDomainAliases } from './hooks/useDomainAliases';
import { DomainFormFields } from './components/DomainFormFields';
import { DomainAliasesTable } from './components/DomainAliasesTable';
import { AliasFormModal } from './components/AliasFormModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { getDomainAliases } from './api/telephonyDomainsApi';
import type { Domain, DomainAlias } from './types';


export default function TelephonyDomainsFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const mode = id === 'new' || duplicateId ? 'create' : 'edit';

  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const { showInfo } = useNotifications();

  // Hook to handle fixed action bar
  useFixedActionBar(true);

  // Clear page title to show tenant logo + name in header

  // Data hooks
  const {
    formData,
    setFormData,
    originalData,
    setOriginalData,
    tenants,
    loadingTenants,
    certificates,
    loadingCertificates
  } = useDomainData(id, duplicateId, mode, resetParam);

  // Form hooks
  const {
    errors,
    hasChanges,
    validateForm,
    handleInputChange,
    inputClasses,
    FORM_ERROR_KEYS
  } = useDomainForm(formData, originalData, mode, setFormData);

  // Submit hooks
  const {
    isSubmitting,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    handleFormSubmit,
    handleCancel: handleCancelSubmit,
    handleDelete,
    confirmDelete
  } = useDomainSubmit(
    formData,
    originalData,
    hasChanges,
    validateForm,
    mode,
    id,
    setFormData,
    setOriginalData
  );

  // Handle save internal (wraps handleFormSubmit to return Domain)
  const handleSaveInternal = useCallback(async (): Promise<Domain | undefined> => {
    // hasChanges validation removed

    const savedData = await handleFormSubmit();
    return savedData;
  }, [handleFormSubmit, hasChanges, showInfo, t, mode, id, formData]);

  // Save with action hook
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/admin/multi-tenant/domains',
    newPath: '/admin/multi-tenant/domains/new',
    getEditPath: (savedData) => `/admin/multi-tenant/domains/${savedData.id}`,
    mode,
    storageKey: 'telephony-domains-save-action',
  });

  // Cancel handler
  const handleCancel = () => {
    handleCancelSubmit();
  };

  // Quick list hooks
  const {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    currentItemId
  } = useQuickList(mode, id, duplicateId);

  // Aliases state and loading
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug ?? 'main';
  const [aliases, setAliases] = useState<DomainAlias[]>([]);
  const [loadingAliases, setLoadingAliases] = useState(false);

  // Load domain aliases
  const loadDomainAliases = useCallback(async (domainId: number | string | undefined, force?: boolean) => {
    if (!domainId) return;
    setLoadingAliases(true);
    try {
      const response = await getDomainAliases(String(domainId), tenantSlug, user?.id);
      setAliases(response.data || []);
    } catch (error) {
      console.error('Failed to load domain aliases:', error);
      setAliases([]);
    } finally {
      setLoadingAliases(false);
    }
  }, [tenantSlug, user?.id]);

  // Load aliases when domain ID changes (edit mode)
  useEffect(() => {
    if (mode === 'edit' && formData.id) {
      loadDomainAliases(formData.id);
    }
  }, [mode, formData.id, loadDomainAliases]);

  // Aliases hooks
  const {
    showAliasForm,
    editingAlias,
    aliasForm,
    aliasErrors,
    openAliasModal,
    closeAliasForm,
    handleAliasInputChange,
    handleAliasSubmit,
    handleEditAlias,
    handleDeleteAlias
  } = useDomainAliases(formData.id, loadDomainAliases);

  return (
    <div className="p-6">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Globe className="h-5 w-5 text-primary" />}
        title={mode === 'create' ? t('telephonyDomains.createDomain') : `${t('telephonyDomains.editDomain')} - ${formData.domainName || ''}`}
        backButtonText={t('common.list')}
        onBack={() => navigate('/admin/multi-tenant/domains')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('telephonyDomains.quickListButton'),
            onClick: () => setShowQuickList(true)
          },
          ...(mode === 'edit' && id && id !== 'new' && !duplicateId
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: () => navigateToTelephonyDomainDuplicate(navigate, id)
              }
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('telephonyDomains.addDomain'),
            onClick: () => navigate('/admin/multi-tenant/domains/new')
          }
        ]}
      />

      <div className="mt-8">
        {/* Form */}
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <FormBox title={t('telephonyDomains.formTitle')} icon={<Globe />}>
            <DomainFormFields
              formData={formData}
              errors={errors}
              tenants={tenants}
              loadingTenants={loadingTenants}
              certificates={certificates}
              loadingCertificates={loadingCertificates}
              onInputChange={handleInputChange}
              inputClasses={inputClasses}
              formErrorKeys={FORM_ERROR_KEYS}
            />
          </FormBox>

          {/* Domain Aliases Section - Only in edit mode */}
          {mode === 'edit' && formData.id && (
            <FormBox
              title={t('telephonyDomains.aliases.title')}
              icon={<Network />}
              className="mt-4"
            >
              {loadingAliases ? (
                <div className="py-4 text-center text-muted-foreground">
                  {t('common.loading')}
                </div>
              ) : (
                <DomainAliasesTable
                  aliases={aliases}
                  onAdd={openAliasModal}
                  onEdit={handleEditAlias}
                  onDelete={handleDeleteAlias}
                />
              )}
            </FormBox>
          )}
        </form>
      </div>

      {/* Alias Form Modal */}
      <AliasFormModal
        isOpen={showAliasForm}
        aliasForm={aliasForm}
        aliasErrors={aliasErrors}
        editingAlias={editingAlias}
        inputClasses={inputClasses}
        onClose={() => closeAliasForm(formData.id)}
        onSubmit={handleAliasSubmit}
        onInputChange={handleAliasInputChange}
      />

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={mode === 'edit' ? handleDelete : undefined}
        deleteDisabled={formData.isPrimary}
        deleteTooltip={formData.isPrimary ? t('telephonyDomains.tooltips.cannotDeletePrimary') : undefined}
        isEditMode={mode === 'edit'}
        saveText={isSubmitting ? t('common.loading') : t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        saveDisabled={isSubmitting}
        isLoading={isSubmitting}
        hasChanges={hasChanges}
        useSaveDropdown={true}
        showSave={true}
        saveDropdownStorageKey="telephony-domains-save-action"
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('telephonyDomains.quickListTitle', 'Quick List - Select Domain')}
        items={filteredQuickList.filter((d): d is typeof d & { id: string } => !!d.id)}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => handleItemSelect(item as Domain)}
        currentItemId={currentItemId}
        renderItem={(domain, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{domain.domainName}</p>
              {domain.domainDescription && (
                <p className="text-sm text-muted-foreground">{domain.domainDescription}</p>
              )}
              {domain.tenantName && (
                <p className="text-xs text-muted-foreground">{domain.tenantName}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('telephonyDomains.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('telephonyDomains.quickListSearchPlaceholder', 'Search by name, description or tenant')}
        emptyMessage={t('telephonyDomains.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('telephonyDomains.noDomainsFoundInSearch', 'No domains found')}
        headerIcon={<Globe className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await confirmDelete();
          // Navigate to list after successful delete
          navigate('/admin/multi-tenant/domains');
        }}
        title={t('telephonyDomains.deleteConfirmTitle')}
        message={t('telephonyDomains.deleteConfirmMessage')}
        confirmText={t('telephonyDomains.deleteConfirm')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
